import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ChevronDown,
  Eye,
  EyeOff,
  TrendingUp,
  Wallet,
  Coins,
  CalendarCheck,
  Sparkles,
} from 'lucide-react';
import axios from 'axios';
import AdminBottomNav from '../../components/AdminBottomNav';
import StaffBottomNav from '../../components/StaffBottomNav';
import StaffVenueHeader from '../../components/staff/StaffVenueHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatEuro(num) {
  const value = Number(num) || 0;
  const fixed = Math.abs(value).toFixed(2);
  const [whole, dec] = fixed.split('.');
  const formattedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${value >= 0 ? '+' : '-'}${formattedWhole},${dec} \u20ac`;
}

function formatEuroShort(num) {
  const value = Number(num) || 0;
  const fixed = Math.abs(value).toFixed(2);
  const [whole, dec] = fixed.split('.');
  const formattedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  return `${sign}${formattedWhole},${dec}\u20ac`;
}

function TipsChart({ dailyData, chartW, chartH, selectedDay, month }) {
  const canvasRef = useRef(null);
  const selIdx = Math.max(0, Math.min(selectedDay - 1, dailyData.length - 1));
  const selData = dailyData[selIdx] || { day: selectedDay, tips: 0, qr_count: 0 };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !dailyData.length) return;
    const dpr = window.devicePixelRatio || 1;
    const cssW = chartW;
    const cssH = chartH;
    if (canvas.width !== cssW * dpr || canvas.height !== cssH * dpr) {
      canvas.width = cssW * dpr;
      canvas.height = cssH * dpr;
    }
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssW, cssH);

    const N = dailyData.length;
    const padX = 14;
    const padTop = 34;
    const padBot = 14;
    const usableW = cssW - padX * 2;
    const usableH = cssH - padTop - padBot;
    const gap = Math.max(2, Math.min(6, (usableW / N) * 0.28));
    const barW = Math.max(3, (usableW - gap * (N - 1)) / N);
    // Bar height = absolute tip amount so higher tips grow UP; on days with
    // no tips the bar collapses to a tiny neutral pill so the axis still
    // reads as populated.
    const values = dailyData.map((d) => Math.max(0, Number(d.tips) || 0));
    const maxV = Math.max(1, ...values);
    const baselineY = cssH - padBot;

    ctx.strokeStyle = 'rgba(0, 0, 0, 0.05)';
    ctx.lineWidth = 1;
    [0.25, 0.5, 0.75].forEach((frac) => {
      const y = baselineY - usableH * frac + 0.5;
      ctx.beginPath();
      ctx.moveTo(padX, y);
      ctx.lineTo(cssW - padX, y);
      ctx.stroke();
    });

    ctx.strokeStyle = 'rgba(0, 0, 0, 0.08)';
    ctx.beginPath();
    ctx.moveTo(padX, baselineY + 0.5);
    ctx.lineTo(cssW - padX, baselineY + 0.5);
    ctx.stroke();

    values.forEach((v, i) => {
      const cx = padX + i * (barW + gap);
      const isSel = i === selIdx;
      const h = v > 0 ? Math.max(6, (v / maxV) * usableH) : 3;
      const y = baselineY - h;
      const r = Math.min(barW / 2, 4);

      const grad = ctx.createLinearGradient(0, y, 0, baselineY);
      if (v === 0) {
        grad.addColorStop(0, 'rgba(150, 150, 165, 0.45)');
        grad.addColorStop(1, 'rgba(150, 150, 165, 0.18)');
      } else if (isSel) {
        grad.addColorStop(0, '#FF9563');
        grad.addColorStop(1, '#FF5316');
      } else {
        grad.addColorStop(0, '#FFC7A6');
        grad.addColorStop(1, '#FF8A47');
      }

      if (isSel && v > 0) {
        ctx.shadowColor = 'rgba(255, 122, 42, 0.35)';
        ctx.shadowBlur = 10;
      }
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.moveTo(cx, baselineY);
      ctx.lineTo(cx, y + r);
      ctx.quadraticCurveTo(cx, y, cx + r, y);
      ctx.lineTo(cx + barW - r, y);
      ctx.quadraticCurveTo(cx + barW, y, cx + barW, y + r);
      ctx.lineTo(cx + barW, baselineY);
      ctx.closePath();
      ctx.fill();
      ctx.shadowBlur = 0;
    });

    const selBarCx = padX + selIdx * (barW + gap) + barW / 2;
    const selValue = values[selIdx];
    const selBarH = selValue > 0 ? Math.max(6, (selValue / maxV) * usableH) : 3;
    const selBarTopY = baselineY - selBarH;

    ctx.beginPath();
    ctx.setLineDash([3, 4]);
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.22)';
    ctx.lineWidth = 1;
    ctx.moveTo(selBarCx, padTop);
    ctx.lineTo(selBarCx, selBarTopY - 6);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.beginPath();
    ctx.arc(selBarCx, selBarTopY - 6, 4, 0, Math.PI * 2);
    ctx.fillStyle = '#FFFFFF';
    ctx.fill();
    ctx.strokeStyle = '#FF5316';
    ctx.lineWidth = 2;
    ctx.stroke();
  }, [dailyData, selIdx, chartW, chartH]);

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: chartH, borderRadius: 12 }} />
      <div
        style={{
          position: 'absolute', left: 10, top: 8,
          fontFamily: "'Inria Serif', serif", fontSize: 10, letterSpacing: 1,
          textTransform: 'uppercase', color: 'var(--text-3)', fontWeight: 700,
        }}
      >
        Daily tips
      </div>
      <div
        style={{
          position: 'absolute', right: 10, top: 4,
          display: 'inline-flex', alignItems: 'center', gap: 8,
          background: 'var(--surface)', border: '1px solid var(--card-border)',
          borderRadius: 999, padding: '4px 10px',
          fontFamily: "'Inria Serif', serif", fontSize: 12, color: 'var(--text)',
          boxShadow: 'var(--premium-shadow-nested)',
        }}
        data-testid="tips-chart-selection-chip"
      >
        <span
          style={{
            width: 7, height: 7, borderRadius: '50%',
            background: 'linear-gradient(180deg, #FF9563, #FF5316)',
            display: 'inline-block',
          }}
        />
        <span style={{ fontWeight: 700 }}>{selData.qr_count} tx</span>
        <span style={{ color: 'var(--text-3)' }}>·</span>
        <span style={{ color: (Number(selData.tips) || 0) > 0 ? 'var(--status-success-text)' : 'var(--text-2)', fontWeight: 700 }}>{formatEuroShort(selData.tips)}</span>
      </div>
      <div style={{ textAlign: 'center', marginTop: 6, fontFamily: "'Inria Serif', serif", fontSize: 13, color: 'var(--text-2)' }} data-testid="tips-chart-selected-day">
        {selData.label ? selData.label : `${selData.day} ${MONTHS[month]}`}
      </div>
    </div>
  );
}

function buildDelta(current, prev) {
  if (!prev || prev <= 0) return null;
  const pct = Math.round(((current - prev) / prev) * 100);
  if (pct <= 0) return null;
  return { pct };
}

function DeltaPill({ pct, testId }) {
  if (!pct) return null;
  return (
    <span
      data-testid={testId}
      style={{
        marginTop: 4,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        borderRadius: 9999,
        background: 'rgba(0, 179, 65, 0.10)',
        color: '#00B341',
        fontFamily: "'Inria Serif', serif",
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.1,
      }}
    >
      <TrendingUp size={11} strokeWidth={2.5} />
      +{pct}% vs last
    </span>
  );
}

const statTileStyle = {
  flex: 1,
  minWidth: 0,
  padding: '12px 12px 14px',
  background: 'var(--surface)',
  border: '1px solid var(--card-border)',
  borderRadius: 16,
  boxShadow: 'var(--premium-shadow-nested)',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const statIconWrapStyle = (bg, fg) => ({
  width: 28,
  height: 28,
  borderRadius: 8,
  background: bg,
  color: fg,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
});

function formatAmountShort(num) {
  if (!num) return '—';
  return `\u20ac${Number(num).toFixed(2).replace(/\.00$/, '')}`;
}

export default function AdminTipsChartPage({ basePath = '/staff-admin', navType = 'admin' }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [rangeMode, setRangeMode] = useState('range'); // 'range' (Last 30 days) | 'month'
  const [selectedDay, setSelectedDay] = useState(30);
  const [data, setData] = useState(null);
  const [hideTotal, setHideTotal] = useState(true);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const sliderRef = useRef(null);
  const isDragging = useRef(false);
  const chartW = 320;
  const chartH = 200;

  const generateEmptyData = useCallback(() => {
    let daily;
    if (rangeMode === 'range') {
      const now = new Date();
      daily = Array.from({ length: 30 }, (_, i) => {
        const d = new Date(now);
        d.setDate(now.getDate() - (29 - i));
        const label = `${d.getDate()} ${MONTHS[d.getMonth()]}`;
        return { day: d.getDate(), label, month_idx: d.getMonth(), tips: 0, qr_count: 0 };
      });
    } else {
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      daily = Array.from({ length: daysInMonth }, (_, i) => ({
        day: i + 1,
        label: `${i + 1} ${MONTHS[month]}`,
        month_idx: month,
        tips: 0,
        qr_count: 0,
      }));
    }
    setData({
      daily_data: daily,
      monthly_tips: 0,
      monthly_qrs: 0,
      total_tips: 0,
      prev_monthly_tips: 0,
      prev_monthly_qrs: 0,
      mom_tips_growth_pct: 0,
      mom_qr_growth_pct: 0,
      avg_tip: 0,
      best_tip_day: null,
      best_tip_day_label: null,
      best_tip_day_amount: 0,
      biggest_tip: 0,
      mode: rangeMode,
      range_days: rangeMode === 'range' ? 30 : null,
    });
  }, [year, month, rangeMode]);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const url = rangeMode === 'range'
        ? `${API}/venue/tips-analytics?range_days=30`
        : `${API}/venue/tips-analytics?year=${year}&month=${month + 1}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      generateEmptyData();
    }
  }, [year, month, rangeMode, generateEmptyData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const dailyData = data?.daily_data || [];
  const daysInMonth = dailyData.length || 30;

  const handleSliderInteraction = useCallback((clientX) => {
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    setSelectedDay(Math.round(ratio * (daysInMonth - 1)) + 1);
  }, [daysInMonth]);

  const handleSliderDown = (e) => {
    isDragging.current = true;
    handleSliderInteraction(e.touches ? e.touches[0].clientX : e.clientX);
  };
  const handleSliderMove = useCallback((e) => {
    if (!isDragging.current) return;
    handleSliderInteraction(e.touches ? e.touches[0].clientX : e.clientX);
  }, [handleSliderInteraction]);
  const handleSliderUp = useCallback(() => { isDragging.current = false; }, []);

  useEffect(() => {
    document.addEventListener('mousemove', handleSliderMove);
    document.addEventListener('mouseup', handleSliderUp);
    document.addEventListener('touchmove', handleSliderMove);
    document.addEventListener('touchend', handleSliderUp);
    return () => {
      document.removeEventListener('mousemove', handleSliderMove);
      document.removeEventListener('mouseup', handleSliderUp);
      document.removeEventListener('touchmove', handleSliderMove);
      document.removeEventListener('touchend', handleSliderUp);
    };
  }, [handleSliderMove, handleSliderUp]);

  const sliderRatio = (selectedDay - 1) / Math.max(daysInMonth - 1, 1);
  const BottomNav = navType === 'staff' ? StaffBottomNav : AdminBottomNav;
  const testPrefix = navType === 'staff' ? 'staff' : 'admin';

  const monthlyTips = Number(data?.monthly_tips || 0);
  const monthlyQrs = Number(data?.monthly_qrs || 0);
  const prevMonthlyTips = Number(data?.prev_monthly_tips || 0);
  const prevMonthlyQrs = Number(data?.prev_monthly_qrs || 0);
  const tipsDelta = buildDelta(monthlyTips, prevMonthlyTips);
  const qrsDelta = buildDelta(monthlyQrs, prevMonthlyQrs);

  const totalTips = Number(data?.total_tips || 0);
  const allTimeDeltaPct = prevMonthlyTips > 0 ? ((monthlyTips - prevMonthlyTips) / prevMonthlyTips) * 100 : 0;
  const allTimeDeltaRounded = Math.round(allTimeDeltaPct);
  const showAllTimeDelta = prevMonthlyTips > 0 && allTimeDeltaRounded > 0;

  const avgTip = Number(data?.avg_tip || 0);
  const bestTipDay = data?.best_tip_day;
  const bestTipDayLabel = data?.best_tip_day_label;
  const bestTipDayAmount = Number(data?.best_tip_day_amount || 0);
  const biggestTip = Number(data?.biggest_tip || 0);
  const isRange = data?.mode === 'range';
  const rangeDays = Number(data?.range_days || 30);
  const periodLabel = isRange ? `Last ${rangeDays} days tips` : 'Monthly tips';
  const deltaSuffix = isRange ? `this ${rangeDays}d vs prior ${rangeDays}d` : 'this month vs last';

  return (
    <div
      className="app-shell premium-page"
      data-testid={`${testPrefix}-tips-page`}
      style={{
        minHeight: '100dvh',
        backgroundColor: 'var(--app-bg)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingBottom: 120,
      }}
    >
      <StaffVenueHeader roleLabel={navType === 'staff' ? 'Venue staff' : 'Admin staff'} testIdPrefix={`${testPrefix}-tips`} />

      {/* Range toggle: Last 30 days vs Monthly */}
      <div style={{ width: '100%', maxWidth: 360, padding: '0 10px', marginTop: 20 }}>
        <div
          role="tablist"
          style={{
            display: 'flex',
            background: 'var(--surface-2)',
            border: '1px solid var(--card-border)',
            borderRadius: 12,
            padding: 4,
            gap: 4,
          }}
        >
          {[
            { value: 'range', label: 'Last 30 days', testId: `${testPrefix}-tips-range-toggle-30d` },
            { value: 'month', label: 'Monthly', testId: `${testPrefix}-tips-range-toggle-month` },
          ].map((opt) => {
            const active = rangeMode === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setRangeMode(opt.value)}
                data-testid={opt.testId}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: 'none',
                  borderRadius: 9,
                  cursor: 'pointer',
                  fontFamily: "'Inria Serif', serif",
                  fontSize: 14,
                  fontWeight: 700,
                  background: active ? 'var(--surface)' : 'transparent',
                  color: active ? 'var(--accent-purple)' : 'var(--text-3)',
                  boxShadow: active ? 'var(--premium-shadow-nested)' : 'none',
                  transition: 'background 160ms ease, color 160ms ease',
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {rangeMode === 'month' ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => { setShowYearPicker(!showYearPicker); setShowMonthPicker(false); }}
                data-testid="tips-year-btn"
                style={{ display: 'flex', alignItems: 'center', gap: 4, fontFamily: "'Inria Serif', serif", fontSize: 20, fontWeight: 700, color: 'var(--text)', background: 'var(--surface)', border: '1px solid var(--card-border)', borderRadius: 14, padding: '8px 14px', cursor: 'pointer', boxShadow: 'var(--premium-shadow-nested)' }}
              >
                {year} <ChevronDown size={16} />
              </button>
              {showYearPicker && (
                <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 20, background: 'var(--surface)', borderRadius: 14, border: '1px solid var(--card-border)', boxShadow: 'var(--shadow-2)', marginTop: 6, overflow: 'hidden' }}>
                  {[2024, 2025, 2026, 2027].map((y) => (
                    <button key={y} onClick={() => { setYear(y); setShowYearPicker(false); }} style={{ display: 'block', width: '100%', padding: '8px 20px', fontFamily: "'Inria Serif', serif", fontSize: 16, border: 'none', background: y === year ? 'var(--accent-surface)' : 'transparent', cursor: 'pointer', textAlign: 'left', color: y === year ? 'var(--accent-purple)' : 'var(--text)' }}>{y}</button>
                  ))}
                </div>
              )}
            </div>

            <div style={{ position: 'relative' }}>
              <button
                onClick={() => { setShowMonthPicker(!showMonthPicker); setShowYearPicker(false); }}
                data-testid="tips-month-btn"
                style={{ display: 'flex', alignItems: 'center', gap: 4, fontFamily: "'Inria Serif', serif", fontSize: 20, fontWeight: 400, color: 'var(--text)', background: 'var(--surface)', border: '1px solid var(--card-border)', borderRadius: 14, padding: '8px 14px', cursor: 'pointer', boxShadow: 'var(--premium-shadow-nested)' }}
              >
                <ChevronDown size={16} /> {MONTHS[month]}
              </button>
              {showMonthPicker && (
                <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 20, background: 'var(--surface)', borderRadius: 14, border: '1px solid var(--card-border)', boxShadow: 'var(--shadow-2)', marginTop: 6, maxHeight: 200, overflowY: 'auto' }}>
                  {MONTHS.map((m, i) => (
                    <button key={m} onClick={() => { setMonth(i); setShowMonthPicker(false); }} style={{ display: 'block', width: '100%', padding: '8px 20px', fontFamily: "'Inria Serif', serif", fontSize: 16, border: 'none', background: i === month ? 'var(--accent-surface)' : 'transparent', cursor: 'pointer', textAlign: 'left', color: i === month ? 'var(--accent-purple)' : 'var(--text)' }}>{m}</button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 10, fontFamily: "'Inria Serif', serif", fontSize: 13, color: 'var(--text-3)', textAlign: 'center' }}>
            Rolling window — updated live
          </div>
        )}
      </div>

      {/* Chart card */}
      <div
        data-testid={`${testPrefix}-tips-chart-card`}
        className="premium-card"
        style={{ width: 'calc(100% - 32px)', maxWidth: 360, marginTop: 16, borderRadius: 22, padding: '14px 16px 12px', position: 'relative' }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, paddingBottom: 6 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0, textAlign: 'left' }}>
            <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 11, letterSpacing: 1.1, textTransform: 'uppercase', color: 'var(--text-3)', fontWeight: 700 }}>
              {periodLabel}
            </span>
            <span data-testid={`${testPrefix}-tips-total-monthly`} style={{ fontFamily: "'Inria Serif', serif", fontSize: 20, fontWeight: 700, color: '#00B341', marginTop: 2, lineHeight: 1.05 }}>
              {formatEuro(monthlyTips)}
            </span>
            <DeltaPill pct={tipsDelta?.pct} testId={`${testPrefix}-tips-delta`} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', textAlign: 'right' }}>
            <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 11, letterSpacing: 1.1, textTransform: 'uppercase', color: 'var(--text-3)', fontWeight: 700 }}>
              Transactions
            </span>
            <span data-testid={`${testPrefix}-tips-total-transactions`} style={{ fontFamily: "'Inria Serif', serif", fontSize: 20, fontWeight: 700, color: 'var(--text)', marginTop: 2, lineHeight: 1.05 }}>
              {monthlyQrs.toLocaleString()}
            </span>
            <DeltaPill pct={qrsDelta?.pct} testId={`${testPrefix}-tips-qrs-delta`} />
          </div>
        </div>

        <TipsChart dailyData={dailyData} chartW={chartW} chartH={chartH} selectedDay={selectedDay} month={month} />

        {/* Slider */}
        <div
          ref={sliderRef}
          onMouseDown={handleSliderDown}
          onTouchStart={handleSliderDown}
          data-testid={`${testPrefix}-tips-slider`}
          style={{ width: '100%', height: 30, position: 'relative', cursor: 'pointer', marginTop: 4, touchAction: 'none' }}
        >
          <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: 2, backgroundColor: 'var(--text-2)', transform: 'translateY(-50%)' }} />
          <div style={{ position: 'absolute', top: '50%', left: `${sliderRatio * 100}%`, width: 14, height: 14, borderRadius: '50%', backgroundColor: 'var(--accent-purple)', transform: 'translate(-50%, -50%)', boxShadow: 'var(--premium-shadow-main)' }} />
        </div>
      </div>

      {/* All-time tipping card */}
      <div style={{ width: '100%', maxWidth: 360, padding: '0 16px', marginTop: 22 }}>
        <div
          data-testid={`${testPrefix}-tips-alltime-card`}
          className="premium-card"
          style={{
            width: '100%',
            padding: '16px 18px 18px',
            borderRadius: 22,
            position: 'relative',
            background: 'var(--surface)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span
                aria-hidden
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 12,
                  background: 'rgba(0, 179, 65, 0.10)',
                  color: '#00B341',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Wallet size={18} strokeWidth={2.2} />
              </span>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 11, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--text-3)', fontWeight: 700 }}>
                  All-time tipping
                </span>
                <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 13, color: 'var(--text-3)', marginTop: 2 }}>
                  Since you joined Allure
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setHideTotal(!hideTotal)}
              data-testid={`${testPrefix}-tips-hide-total-button`}
              aria-label={hideTotal ? 'Show all-time tipping' : 'Hide all-time tipping'}
              style={{
                background: 'var(--surface-2)',
                border: '1px solid var(--card-border)',
                borderRadius: 9999,
                padding: '6px 10px',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                color: 'var(--text-2)',
                fontFamily: "'Inria Serif', serif",
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              {hideTotal ? <Eye size={14} strokeWidth={2.2} /> : <EyeOff size={14} strokeWidth={2.2} />}
              {hideTotal ? 'Show' : 'Hide'}
            </button>
          </div>

          <div
            data-testid={`${testPrefix}-tips-alltime-amount`}
            style={{
              marginTop: 14,
              fontFamily: "'Inria Serif', serif",
              fontWeight: 700,
              fontSize: 32,
              lineHeight: 1.1,
              color: hideTotal ? 'var(--text-3)' : '#00B341',
              letterSpacing: hideTotal ? 4 : -0.5,
            }}
          >
            {hideTotal ? '••••••' : formatEuro(totalTips)}
          </div>

          {showAllTimeDelta && (
            <div
              data-testid={`${testPrefix}-tips-mom-delta`}
              style={{
                marginTop: 10,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px',
                borderRadius: 9999,
                background: 'rgba(0, 179, 65, 0.08)',
                color: '#00B341',
                fontFamily: "'Inria Serif', serif",
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              <TrendingUp size={13} strokeWidth={2.5} />
              +{allTimeDeltaRounded}% {deltaSuffix}
            </div>
          )}
        </div>
      </div>

      {/* Tipping-specific stat tiles */}
      <div
        data-testid={`${testPrefix}-tips-stats-tiles`}
        style={{ width: '100%', maxWidth: 360, padding: '0 16px', marginTop: 18, display: 'flex', gap: 10 }}
      >
        <div data-testid={`${testPrefix}-tips-avg-tip`} style={statTileStyle}>
          <span style={statIconWrapStyle('rgba(0, 179, 65, 0.10)', '#00B341')}>
            <Coins size={16} strokeWidth={2} />
          </span>
          <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>Avg tip</span>
          <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{formatAmountShort(avgTip)}</span>
        </div>

        <div data-testid={`${testPrefix}-tips-best-day`} style={statTileStyle}>
          <span style={statIconWrapStyle('var(--accent-surface-soft)', 'var(--accent-purple)')}>
            <CalendarCheck size={16} strokeWidth={2} />
          </span>
          <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>Best tip day</span>
          <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>
            {bestTipDayLabel || (bestTipDay ? `${bestTipDay} ${MONTHS[month]}` : '—')}
          </span>
          <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 12, color: '#00B341', fontWeight: 700 }}>
            {bestTipDayAmount ? formatAmountShort(bestTipDayAmount) : ''}
          </span>
        </div>

        <div data-testid={`${testPrefix}-tips-biggest-tip`} style={statTileStyle}>
          <span style={statIconWrapStyle('rgba(229, 57, 53, 0.10)', '#E53935')}>
            <Sparkles size={16} strokeWidth={2} />
          </span>
          <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>Biggest tip</span>
          <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{formatAmountShort(biggestTip)}</span>
        </div>
      </div>

      <BottomNav basePath={basePath} />
    </div>
  );
}
